<template>
    <article class="document mr-12@md">
        <div v-if="thumbBase64" class="document__thumb-wrapper">
            <img :src="thumbBase64" class="document__thumb" alt="" />
            <div v-if="source" class="document__ribbon">
                <span class="document__ribbon__inner" :class="`document__ribbon__inner--${source}`">
                    <span class="document__ribbon__text u-block">{{ source.toUpperCase() }}</span>
                </span>
            </div>
        </div>
        <div v-else class="document__thumb document__thumb--dummy u-flex u-flex--center">
            <spinner/>
        </div>

        <div class="u-flex u-flex--center">
            <div class="u-text-center mr-12">
                <h3 class="document__title mb-0">{{ name }}</h3>
                <small v-if="date" class="m-0 text-mute u-block">{{ formattedDate }}</small>
            </div>

            <circle-button
                class="document__menu"
                title="Aktionen"
                icon="three-bars"
                size="small"
                @click.native.stop="showActionModal = true"
            />
        </div>

        <circle-button
            class="document__view p-0"
            title="Anschauen"
            icon="eye"
            theme="primary"
            :is-loading="isLoadingView"
            size="medium"
            @click.native="onView"
        />

        <confirm-modal
            v-if="showDeleteModal"
            v-model="showDeleteModal"
            headline="Löschen"
            :is-loading="isLoadingDelete"
            @confirm="onDelete"
        >
            <p class="m-0">
                <template v-if="persistDelete">Möchtest du das Dokument "{{ name }}" wirklich endgültig löschen?</template>
                <template v-else>Möchtest du das Dokument "{{ name }}" wirklich in den Papierkorb verschieben?</template>
            </p>
        </confirm-modal>

        <confirm-modal
            v-if="showArchiveModal"
            v-model="showArchiveModal"
            headline="Archivieren"
            :is-loading="isLoadingArchive"
            @confirm="onArchive"
        >
            <p class="m-0">Möchtest du das Dokument "{{ name }}" wirklich archivieren?</p>
        </confirm-modal>

        <add-document-modal
            v-if="showEditModal"
            v-model="showEditModal"
            document-status="draft"
            :document="document"
            @add="onEdit"
        />

        <document-action-modal
            v-if="showActionModal"
            v-model="showActionModal"
            :description="description"
            :tags="tags"
            :show-archive="showArchive"
            :persist-delete="persistDelete"
            @edit="onClickEdit"
            @archive="onClickArchive"
            @delete="onClickDelete"
        />
    </article>
</template>

<script>
import { directive as onClickAway } from 'vue-clickaway'
import CircleButton from '@/components/ui/CircleButton.vue'
import ConfirmModal from '@/components/ui/modals/ConfirmModal.vue'
import AddDocumentModal from '@/components/ui/modals/AddDocumentModal.vue'
import DocumentActionModal from '@/components/ui/modals/DocumentActionModal.vue'
import Spinner from '@/components/ui/Spinner.vue'

export default {
    name: 'DocumentThumb',

    components: { Spinner, DocumentActionModal, AddDocumentModal, ConfirmModal, CircleButton },

    directives: { onClickAway },

    props: {
        id: { type: String, required: true },
        thumb: { type: String, required: true },
        name: { type: String, required: true },
        url: { type: String, required: true },
        date: { type: String, default: null },
        persistDelete: { type: Boolean, default: false },
        tags: { type: Array, default: () => [] },
        showArchive: { type: Boolean, default: false },
        source: { type: String, default: '', validator: val => ['', 'mail', 'cloud'].includes(val) },
        description: { type: String, default: '' },
    },

    data: () => ({
        showEditModal: false,
        showArchiveModal: false,
        showActionModal: false,
        isLoadingView: false,
        isLoadingEdit: false,
        showDeleteModal: false,
        isLoadingDelete: false,
        isLoadingArchive: false,

        thumbBase64: null,
    }),

    computed: {
        document () {
            return { id: this.id, name: this.name, date: this.date, tags: this.tags, description: this.description }
        },

        thumbUrl () {
            return `${process.env.VUE_APP_API_BASE_URL}${this.thumb}`
        },

        formattedDate () {
            return this.$date(new Date(this.date), 'dd.MM.yyyy')
        },
    },

    mounted () {
        this.loadThumb()
    },

    methods: {
        onClickEdit () {
            this.showEditModal = true
            this.showActionModal = false
        },

        onClickArchive () {
            this.showArchiveModal = true
            this.showActionModal = false
        },

        onClickDelete () {
            this.showDeleteModal = true
            this.showActionModal = false
        },

        async onView () {
            this.isLoadingView = true

            try {
                const response = await this.$http.get(`${process.env.VUE_APP_API_BASE_URL}/get-document-file-token?documentId=${this.id}`)
                if (response.data?.error) {
                    throw new Error(response.data.error)
                }

                const viewUrl = `${process.env.VUE_APP_API_BASE_URL}${this.url}?at=${response.data}`

                setTimeout(() => {
                    // Timeout is needed for iOS
                    // @see https://stackoverflow.com/a/70463940/3356082
                    window.open(viewUrl, '_blank', 'noreferrer')
                })
            } catch (e) {
                console.log(e)
                this.$toast.open({ type: 'error', message: e.message })
            } finally {
                this.isLoadingView = false
            }
        },

        onEdit () {
            this.$emit('reload')
            this.showEditModal = false
        },

        async loadThumb () {
            try {
                const response = await this.$http.get(this.thumbUrl, { responseType: 'blob' })

                const reader = new FileReader();
                reader.onloadend = (event) => {
                    this.thumbBase64 = event.target.result
                }
                reader.readAsDataURL(response.data);
            } catch (e) {
                console.log(e)
            }
        },

        async onDelete () {
            try {
                this.isLoadingDelete = true

                let response = null
                if (this.persistDelete) {
                    response = await this.$http.delete(`/document/delete/${this.id}`)
                } else {
                    response = await this.$http.post(`/document/trash/${this.id}`)
                }

                if (response.data?.error) {
                    throw new Error(response.data.error)
                }

                this.$toast.open({ type: 'success', message: response.data.successHtml })
                this.$emit('delete', this.id)
            } catch (e) {
                console.log(e)
                this.$toast.open({ type: 'error', message: e.message })
            } finally {
                this.isLoadingDelete = false
            }
        },

        async onArchive () {
            try {
                this.isLoadingArchive = true

                const response = await this.$http.post(`/document/archive/${this.id}`)

                if (response.data?.error) {
                    throw new Error(response.data.error)
                }

                this.$toast.open({ type: 'success', message: response.data.successHtml })
                this.$emit('reload')
                this.$emit('input', false)
            } catch (e) {
                console.log(e)
                this.$toast.open({ type: 'error', message: e.message })
            } finally {
                this.isLoadingArchive = false
            }
        },
    },
}
</script>

<style lang="scss" scoped>
$ribbon-size: 5em;
$ribbon-font-size: 0.75rem;
$ribbon-translate-x: -1.8em;

.document {
    position: relative;
    padding: $spacing-unit-12;
    cursor: pointer;
    max-width: 100%;

    @include interaction {
        &:after {
            content: "";
            display: block;
            width: 100%;
            height: 100%;
            background-color: rgba($primary-color, 0.1);
            position: absolute;
            top: 0;
            left: 0;
            z-index: 0;
        }

        .document__view {
            display: flex !important;
        }
    }

    &__thumb-wrapper {
        position: relative;
        border: $spacing-unit-3 solid $tertiary-color;
    }

    &__thumb {
        object-fit: cover;
        width: $document-width;
        height: $document-height;

        &--dummy {
            background: $tertiary-color;
        }

        @include media-breakpoint-down(sm) {
            max-width: 100%;
        }
    }

    &__title {
        font-size: 0.75rem;
        margin-top: $spacing-unit-6;
        max-width: $document-width;
    }

    &__menu {
        position: relative;
        z-index: $index-above;
        background: transparent;
    }

    &__view {
        display: none !important;
        margin: auto;
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        z-index: $index-above;
    }

    &__ribbon {
        position: absolute;
        top: 0;
        left: 0;
        height: $ribbon-size;
        width: $ribbon-size;

        &__inner {
            position: absolute;
            inset: 0;
            clip-path: polygon(0 0, 100% 0, 0 100%);

            &--mail {
                background: $highlight-color;
                color: $white;
            }

            &--cloud {
                background: $primary-color;
                color: $white;
            }
        }

        &__text {
            font-size: $ribbon-font-size;
            text-align: center;
            transform: rotate(-45deg) translateX($ribbon-translate-x);
        }
    }
}

/deep/ .dropdown-toggle:after {
    display: none;
}

/deep/ .dropdown-item.active,
/deep/ .dropdown-item:active {
    background-color: $primary-color !important;
}
</style>
